<script setup lang="ts">
import {computed, ref, watch, inject, nextTick} from "vue";
import {useFormDataStore} from "@/stores/formDataStore";
import { useI18n } from 'vue-i18n';
import type { FormEnergyCommunity } from '@/stores/communityApiStore';
import type { FormValidation } from '@/types/form-validation';

import iban from 'iban';
import {ibanToBic} from 'iban-to-bic';
import DOMPurify from 'dompurify';

const { t } = useI18n();
const formDataStore = useFormDataStore();
const formValidation = inject('$formValidation') as FormValidation;

interface Props {
  communityPublic?: FormEnergyCommunity;
  isActive?: boolean;
}


const props = defineProps<Props>()

import { shouldSkipWireInfo } from '@/lib/utils/communityFlags';
const skipWireInfo = computed(()=> shouldSkipWireInfo(props.communityPublic?.ecn));

const wireInfoValidation = computed(() => {
  const validators: Array<(value: unknown) => boolean | string> = [];

  if(skipWireInfo.value === true)
    return validators;

  validators.push((value: unknown) => formValidation.formValidationRequired(value as string | null | undefined))

  return validators
})

watch(() => formDataStore.formData.billingData.wireInfo.iban,
  (ibanInput: string) => {
    ibanInput = ibanInput.replace(/\s/g,'');
    if(ibanInput.length && iban.isValid(ibanInput)) {
      // IBAN valid > guess BIC

      if(formDataStore.formData.billingData.wireInfo.bic?.length === 0) {
        // only if BIC is empty

        formDataStore.formData.billingData.wireInfo.bic=ibanToBic(ibanInput) || "";
      }

    }
  })

import { METERING_POINT_DIRECTION } from "@/lib/constants/meteringPoint";

type MeteringPointMinimal = { meteringPointDirection: string };
const atLeastOneProducer = computed(() => {
  return formDataStore.formData.meteringPointsAssociated.some((mp: MeteringPointMinimal) => mp.meteringPointDirection === METERING_POINT_DIRECTION.GENERATION);
});

const vatNormal = 'USt.-pflichtig';
const vatFlat = 'Pauschalierung (Land-/Forstwirt)';
const vatLittleOne = 'Kleinunternehmer (unecht Steuerbefreit)';
const vatPrivate = 'Nichtunternehmer lt. Photovoltaikerlass';
const vatManual = 'selbst eingeben';
const vatUnknown = 'unbekannt';

const itemsVat= [
  vatNormal,
  vatPrivate,
  vatFlat,
  vatLittleOne,
  vatManual,
];

const uidInput = ref<{ validate: () => Promise<boolean> } | null>(null);

watch(() => formDataStore.formData.billingData.taxInfo.taxSelection,
  async () => {
    if(vatTypeNormal.value) {
      formDataStore.formData.billingData.taxInfo.taxRatePercentPoints=20;
      formDataStore.formData.billingData.taxInfo.taxNote="";
    } else if(vatTypeFlat.value) {
      formDataStore.formData.billingData.taxInfo.taxRatePercentPoints=13;
      formDataStore.formData.billingData.taxInfo.taxNote="Umsatzsteuerpauschalierung lt. § 22 UStG";
    } else if(vatTypeLittleOne.value) {
      formDataStore.formData.billingData.taxInfo.taxRatePercentPoints=0;
      formDataStore.formData.billingData.taxInfo.taxNote="USt-Befreiung gem.  § 6 (1) Z. 27 UStG";
    } else if(vatTypePrivate.value) {
      formDataStore.formData.billingData.taxInfo.taxRatePercentPoints=0;
      formDataStore.formData.billingData.taxInfo.taxNote="Nicht steuerbar (Nichtunternehmer lt. §12 Abs 2 Z 2 lit. a UStG)";
    } else if(vatTypeUnknown.value) {
      formDataStore.formData.billingData.taxInfo.taxRatePercentPoints=0;
      formDataStore.formData.billingData.taxInfo.taxNote="Umsatzsteuereigenschaften werden nachgereicht";
    } else { // vatTypeManual
      formDataStore.formData.billingData.taxInfo.taxRatePercentPoints=0;
      formDataStore.formData.billingData.taxInfo.taxNote="";
    }

    await uidInput.value?.validate();
  });

watch(() => formDataStore.formData.billingData.taxInfo.taxRatePercentPoints,
  async () => {
    await uidInput.value?.validate();
  });

const vatTypeNormal = computed(() =>
  formDataStore.formData.billingData.taxInfo.taxSelection === vatNormal);
const vatTypeFlat = computed(() =>
  formDataStore.formData.billingData.taxInfo.taxSelection === vatFlat);
const vatTypeLittleOne = computed(() =>
  formDataStore.formData.billingData.taxInfo.taxSelection === vatLittleOne);
const vatTypePrivate = computed(() =>
  formDataStore.formData.billingData.taxInfo.taxSelection === vatPrivate);
const vatTypeManual = computed(() =>
  formDataStore.formData.billingData.taxInfo.taxSelection === vatManual);
const vatTypeUnknown = computed(() =>
  formDataStore.formData.billingData.taxInfo.taxSelection === vatUnknown);

const formValid = ref(false);
const theForm = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

async function formValidates() {
  if (!theForm.value) return true;
  const result = await theForm.value.validate();
  const valid = result.valid;

  if (!valid) {
    formValidation.scrollToFirstValidationError({
      nextTick
    });
  }

  return valid;
}
defineExpose({formValidates});

const sanitizedVatDetailsMessage = computed(() => {
  const rawHtml = t('vatMessageJoin2StayHtml');
  return DOMPurify.sanitize(rawHtml);
});
</script>

<template>
  <div>
    <div class="background-divider" v-if="atLeastOneProducer"></div>

    <div class="pa-4" v-if="atLeastOneProducer">
      <p class="textFormHeadline">{{ t('vatDetailsJoin2Stay') }}</p>
      <div class="textDescriptor" v-html="sanitizedVatDetailsMessage">
      </div>
    </div>

    <div class="background-divider"></div>

    <div class="pa-4">
    <v-form ref="theForm" v-model="formValid">
      <v-row no-gutters>
        <v-col cols="12" sm="8">
          <p class="textFormHeadline" v-if="atLeastOneProducer">{{ t('vatDetails') }}</p>

          <v-row no-gutters v-if="atLeastOneProducer">
            <v-col cols="10">
              <v-select
                :label="t('billingLabels.vatType')" class="requiredInput"
                variant="outlined"
                :items="itemsVat"
                v-model="formDataStore.formData.billingData.taxInfo.taxSelection"
                :rules="[formValidation.formValidationRequired]"
              ></v-select>
            </v-col>

            <v-col cols="4" v-if="vatTypeManual">
              <v-text-field :label="t('billingLabels.vatRate')" class="requiredInput"
                            variant="outlined" hint="" suffix="%"
                            v-model="formDataStore.formData.billingData.taxInfo.taxRatePercentPoints"
                            :rules="[formValidation.formValidationTaxRate]"
              ></v-text-field>
            </v-col>

            <v-col cols="8" v-if="vatTypeManual">
              <v-text-field :label="t('billingLabels.note')" class="requiredInput"
                            variant="outlined"
                            :hint="t('billingLabels.noteHint')"
                            v-model="formDataStore.formData.billingData.taxInfo.taxNote"

              ></v-text-field>
            </v-col>

            <v-col cols="10" v-if="vatTypeNormal">
              {{ t('vatRate') }}: 20% <br/>
            </v-col>

            <v-col cols="10" v-if="vatTypeFlat">
              {{ t('vatRate') }}: 13% <br/>
              {{ t('vatNote') }}: Umsatzsteuerpauschalierung lt. § 22 UStG<br/>
            </v-col>

            <v-col cols="10" v-if="vatTypeLittleOne">
              {{ t('vatRate') }}: 0% <br/>
              {{ t('vatNote') }}: USt-Befreiung gem.  § 6 (1) Z. 27 UStG<br/>
            </v-col>
            <v-col cols="10" v-if="vatTypePrivate">
              {{ t('vatRate') }}: 0% <br/>
              {{ t('vatNote') }}: Nicht steuerbar (Nichtunternehmer lt. §12 Abs 2 Z 2 lit. a UStG)<br/>
            </v-col>
            <v-col cols="10" v-if="vatTypeUnknown">
              Umsatzsteuereigenschaften werden nachgereicht<br/>
            </v-col>


            <v-col cols="6">
              <br v-if="!vatTypeManual"/>
              <v-text-field ref="uidInput" :label="t('billingLabels.uidNumber')"
                            :hint="t('billingLabels.uidNumberHint')"
                            variant="outlined"
                            v-bind:class="{requiredInput: vatTypeNormal}"
                            v-model="formDataStore.formData.billingData.taxInfo.uid"
                            :rules="[(value) => formValidation.formValidationRequiredOnNormalVat(value, vatTypeNormal, vatTypeManual, formDataStore),
                              formValidation.formValidationVat]"></v-text-field>
            </v-col>
          </v-row>


          <div v-if="!skipWireInfo">
            <p class="textFormHeadline">{{ t('bankDetails') }}</p>
            <v-text-field :label="t('billingLabels.accountHolder')" variant="outlined" class="requiredInput"
                          v-model="formDataStore.formData.billingData.wireInfo.accountHolder"
                          :rules="[...wireInfoValidation]"></v-text-field>
            <v-text-field :label="t('billingLabels.iban')" variant="outlined" class="requiredInput"
                          v-model="formDataStore.formData.billingData.wireInfo.iban"
                          :rules="[...wireInfoValidation, formValidation.formValidationIban]"></v-text-field>
            <v-row no-gutters>
              <v-col cols="6">
                <v-text-field :label="t('billingLabels.bic')" variant="outlined" class="requiredInput"
                              v-model="formDataStore.formData.billingData.wireInfo.bic"
                              :rules="[...wireInfoValidation, formValidation.formValidationBic]"></v-text-field>
              </v-col>
            </v-row>
          </div>

          <p class="textFormHeadline">{{ t('notes') }}</p>
          <p class="textDescriptor">
            {{ t('notesSubmissionMessage') }}
          </p>

          <v-textarea
            no-resize
            variant="outlined"
            v-model="formDataStore.formData.notes"
            rows="4"
          ></v-textarea>
        </v-col>
      </v-row>
    </v-form>
  </div>
  <div class="background-divider"></div>
</div>
</template>
